import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PhotoFramingBar from './PhotoFramingBar';
import { useFramingGestures } from './useFramingGestures';
import { normalizeFraming, ZOOM_MIN } from './framing';
import { getPageMetrics } from './atelierLayoutGeometry';
import { gutterSafetyMmOf } from '../../../utils/printFormats';
import { useTranslation } from 'react-i18next';
import { useFaceNearFold } from './faceFold';
import { applyLiveToPhoto } from './framingLive';

// Tout ce qui s'ajoute a UN emplacement photo SELECTIONNE (phase 4a) : la barre
// flottante, les gestes (glisser, molette, pincement, double-clic), la zone de
// securite du pli sur une double page, et la fermeture (Echap, clic ailleurs).
// Monte DANS l'emplacement de l'incrustation (.atelier-overlay-slot) : il y
// attache ses gestes.
function SlotFramingLayer({
  target, item, occurrence, imageRatio, framing, onFramingChange, onDeselect,
  onReplace, onRemove, spreadSide, printFormat
}) {
  const { t } = useTranslation('atelier');
  const anchorRef = useRef(null);
  const elementRef = useRef(null);
  useLayoutEffect(() => { elementRef.current = anchorRef.current ? anchorRef.current.parentElement : null; });

  const current = normalizeFraming(framing);
  // Element de l'emplacement (parent de l'ancre) : la barre se cale dessus, la bande « Pli » suit son glissement.
  const [slotElement, setSlotElement] = useState(null);
  const [dragging, setDragging] = useState(false);
  useLayoutEffect(() => { setSlotElement(anchorRef.current ? anchorRef.current.parentElement : null); }, []);
  useEffect(() => {
    if (!slotElement || typeof MutationObserver === 'undefined') return undefined;
    const sync = () => setDragging(slotElement.classList.contains('is-framing-drag'));
    const observer = new MutationObserver(sync);
    observer.observe(slotElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [slotElement]);

  useFramingGestures({
    enabled: true, // Remplir : recadrer ; Entiere : deplacer la photo entiere dans son cadre
    elementRef,
    target,
    itemUrls: [item.url, item.metadata && item.metadata.previewUrl],
    occurrence,
    fallbackRatio: imageRatio,
    framing: current,
    onCommit: (next) => onFramingChange({ focusX: next.focusX, focusY: next.focusY, zoom: next.zoom }, { gesture: true })
  });

  // Echap, ou un appui sur une zone NEUTRE DU LIVRE (la page, sa marge) : on referme.
  // Regle volontairement INVERSEE (phase 5 bis) : avant, "tout appui hors de la
  // photo" fermait — donc aussi la barre de defilement du panneau de gauche, ses
  // onglets, ses boutons... et le panneau disparaissait sous la souris. Maintenant
  // seule la colonne du livre peut fermer ; le panneau de gauche ne ferme jamais
  // (il a son bouton "Retour" et Echap), et un changement d'onglet ou de page
  // referme explicitement (voir BookAtelierLuxe).
  useEffect(() => {
    const onPointerDown = (event) => {
      const target = event.target;
      if (!target || !target.closest) return;
      if (!target.closest('.atelier-stage-column')) return;
      if (target.closest('.is-framing-selected, .atelier-framing-bar')) return;
      onDeselect();
    };
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (event.target.closest && event.target.closest('input, textarea')) return;
      onDeselect();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onDeselect]);

  const switchFit = (fit) => {
    if (fit === current.fit) return;
    // "Entiere" : toute la photo est visible, il n'y a plus rien a deplacer ni a
    // zoomer — on remet le zoom a 1 (sinon la photo "entiere" ne le serait plus).
    onFramingChange(fit === 'contain' ? { fit, zoom: ZOOM_MIN } : { fit });
  };

  // Zone de securite du pli (double page) : bande le long du bord INTERIEUR de la
  // page, la ou la reliure mange l'image — reprise de l'ancienne fenetre.
  const metrics = getPageMetrics(printFormat);
  // Zone a eviter au pli : 12 mm en dos colle, AUCUNE en layflat (les pages s'ouvrent a plat, rien n'est perdu au centre).
  const gutterMm = gutterSafetyMmOf(printFormat);
  const foldPct = spreadSide && gutterMm > 0 ? (gutterMm / metrics.contentWidthMm) * 100 : 0;

  const slotRect = slotElement ? slotElement.getBoundingClientRect() : null;
  const frameRatio = slotRect && slotRect.height > 0 ? slotRect.width / slotRect.height : null;
  // Avertissement visage : seulement si un visage est detecte pres du pli (voir faceFold.js).
  const faceWarning = useFaceNearFold({
    enabled: Boolean(spreadSide) && gutterMm > 0,
    url: item.metadata && (item.metadata.previewUrl || item.metadata.thumbnailUrl) ? (item.metadata.previewUrl || item.metadata.thumbnailUrl) : item.url,
    imageRatio,
    frameRatio,
    framing: current,
    spreadSide,
    foldFraction: foldPct / 100
  });

  return (
    <>
      <span ref={anchorRef} className="atelier-framing-anchor" aria-hidden="true" />
      {/* Bande « Pli » : UNIQUEMENT pendant le glissement (correctif 5) — au repos et photo selectionnee, rien. */}
      {spreadSide && foldPct > 0 && dragging && (
        <span
          className={`atelier-fold-zone is-${spreadSide === 'left' ? 'right-edge' : 'left-edge'}`}
          style={{ width: `${foldPct}%` }}
          aria-hidden="true"
        >
          <b>{t('framing.fold')}</b>
        </span>
      )}
      <PhotoFramingBar
        fit={current.fit}
        zoom={current.zoom}
        onFit={switchFit}
        // Le curseur agit EN DIRECT sur le rendu, puis tous ses crans ne forment qu un pas enregistre (un cran = une
        // ecriture + un nouveau rendu : un glissement martelait le serveur).
        onZoom={(zoom) => {
          applyLiveToPhoto({ target, urls: [item.url, item.metadata && item.metadata.previewUrl], occurrence, framing: { ...current, zoom } });
          onFramingChange({ zoom }, { gesture: true });
        }}
        onReplace={onReplace}
        onRemove={onRemove}
        faceWarning={faceWarning}
        anchorElement={slotElement}
      />
    </>
  );
}

export default SlotFramingLayer;
